import { dealStageColorSchema } from '@veem/schema'
import { z } from 'zod'

const version = z.number().int().positive()
const name = z.string().trim().min(1).max(200)
const key = z.string().min(1).max(200)
const direction = z.union([z.literal(-1), z.literal(1)])
const minutes = z.number().int().positive().max(100_000).nullable()
const edrpou = z
  .string()
  .regex(/^(\d{8}|\d{10})$/)
  .nullable()
const decimals = z.union([z.literal(0), z.literal(3)])
const warehouse = name
const nonempty = (patch: object) => Object.keys(patch).length > 0
const stagePatch = z
  .object({ label: name.optional(), color: dealStageColorSchema.optional() })
  .strict()
  .refine(nonempty)
const routePatch = z
  .object({
    label: name.optional(),
    workCenter: name.optional(),
    minutes: minutes.optional(),
    archived: z.boolean().optional(),
  })
  .strict()
  .refine(nonempty)
const companyPatch = z
  .object({ name: name.optional(), edrpou: edrpou.optional() })
  .strict()
  .refine(nonempty)
const profileText = (max: number) => z.string().trim().max(max)
const profilePatch = z
  .object({
    legalName: profileText(300).optional(),
    address: profileText(1000).optional(),
    phone: profileText(200).optional(),
    email: profileText(320).optional(),
    website: profileText(300).optional(),
    altHeader: profileText(2000).optional(),
    signatoryPosition: profileText(200).optional(),
    signatoryName: profileText(200).optional(),
  })
  .strict()
  .refine(nonempty)
export const companyFileKind = z.enum(['logo', 'signature', 'stamp'])
const unitPatch = z
  .object({
    name: name.optional(),
    decimals: decimals.optional(),
    archived: z.boolean().optional(),
  })
  .strict()
  .refine(nonempty)

export const settingsSchemas = {
  updateStage: z.object({ version, key, patch: stagePatch }).strict(),
  setStageArchived: z.object({ version, key, archived: z.boolean() }).strict(),
  moveStage: z.object({ version, key, direction }).strict(),
  addStage: z
    .object({
      version,
      label: name,
      group: z.enum(['open', 'successful', 'unsuccessful']),
      color: dealStageColorSchema,
    })
    .strict(),
  deleteStage: z.object({ version, key }).strict(),
  updateRouteStage: z.object({ version, key, patch: routePatch }).strict(),
  moveRouteStage: z.object({ version, key, direction }).strict(),
  addRouteStage: z.object({ version, label: name, workCenter: name, minutes }).strict(),
  addWorkCenter: z.object({ version, name }).strict(),
  moveWorkCenter: z.object({ version, name, direction }).strict(),
  archiveWorkCenter: z.object({ version, name }).strict(),
  restoreWorkCenter: z.object({ version, name }).strict(),
  renameWorkCenter: z.object({ version, from: name, to: name }).strict(),
  addMachine: z.object({ version, name, workCenter: name }).strict(),
  updateMachine: z
    .object({
      version,
      id: key,
      patch: z
        .object({ name: name.optional(), workCenter: name.optional() })
        .strict()
        .refine(nonempty),
    })
    .strict(),
  setMachineArchived: z.object({ version, id: key, archived: z.boolean() }).strict(),
  updateCompany: z.object({ version, id: key, patch: companyPatch }).strict(),
  makeDefaultCompany: z.object({ version, id: key }).strict(),
  setCompanyActive: z.object({ version, id: key, active: z.boolean() }).strict(),
  addCompany: z.object({ version, name, edrpou }).strict(),
  updateCompanyProfile: z.object({ version, id: key, patch: profilePatch }).strict(),
  // The file itself arrives as multipart form data next to these fields.
  setCompanyFile: z.object({ version, id: key, kind: companyFileKind }).strict(),
  removeCompanyFile: z.object({ version, id: key, kind: companyFileKind }).strict(),
  setCurrencyActive: z.object({ version, code: key, active: z.boolean() }).strict(),
  makeDefaultCurrency: z.object({ version, code: key }).strict(),
  updateVatMode: z.object({ version, key, name }).strict(),
  makeDefaultVat: z.object({ version, key }).strict(),
  setVatActive: z.object({ version, key, active: z.boolean() }).strict(),
  addVatMode: z
    .object({
      version,
      name,
      rate: z
        .string()
        .regex(/^\d{1,3}([.,]\d{1,2})?$/)
        .refine((value) => Number(value.replace(',', '.')) <= 100),
      priceIncludesVat: z.boolean(),
    })
    .strict(),
  addLocation: z.object({ version, warehouse, name }).strict(),
  renameLocation: z.object({ version, warehouse, from: name, to: name }).strict(),
  archiveLocation: z.object({ version, warehouse, location: name }).strict(),
  restoreLocation: z.object({ version, warehouse, location: name }).strict(),
  addCategory: z.object({ version, name }).strict(),
  renameCategory: z.object({ version, from: name, to: name }).strict(),
  archiveCategory: z.object({ version, category: name }).strict(),
  restoreCategory: z.object({ version, category: name }).strict(),
  addUnit: z.object({ version, symbol: name, name, decimals }).strict(),
  updateUnit: z.object({ version, symbol: key, patch: unitPatch }).strict(),
} as const

export type SettingsOperation = keyof typeof settingsSchemas
export type SettingsInput<K extends SettingsOperation> = z.infer<(typeof settingsSchemas)[K]>
export interface SettingsWriteResult {
  version: number
  id?: string
}
